import { useState, useEffect, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router";
import { createPortal } from "react-dom";
import { PageContainer } from "@/app/layouts/PageContainer";
import { PageHeader } from "@/components/page-header";
import { FilterChipBar, type FilterChipOption } from "@/components/filter-chip-bar";
import { Input } from "@/components/ui/input";

export interface ListPageProps<TChip extends string = string> {
  title: string;
  primaryAction?: ReactNode;
  /** Search input — pass to render the filled-grey search field on the toolbar. */
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
  };
  /** Filter chips — single-select chip bar in the toolbar. */
  filterChips?: {
    options: ReadonlyArray<FilterChipOption<TChip>>;
    value: TChip;
    onChange: (next: TChip) => void;
    "aria-label"?: string;
  };
  /** Extra inline content to render on the toolbar, typically to the right of filterChips. */
  toolbarExtra?: ReactNode;
  /** Legacy slot for non-standard list pages. Renders below the toolbar if provided. */
  filterBar?: ReactNode;
  children: ReactNode;
  backTo?: string;
}

export function ListPage<TChip extends string = string>({
  title,
  primaryAction,
  search,
  filterChips,
  toolbarExtra,
  filterBar,
  children,
  backTo,
}: ListPageProps<TChip>) {
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  const [rightPortalNode, setRightPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
    setRightPortalNode(document.getElementById("topbar-right-portal"));
  }, []);

  return (
    <PageContainer>
      {backTo && portalNode && createPortal(
        <Link to={backTo} className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back
        </Link>,
        portalNode
      )}

      <PageHeader title={title} />

      {/* With no toolbar row to host it, the primary action goes to the topbar.
          Fall back to rendering inline when that portal is absent (unit tests,
          print, any non-AppShell render) so the action is never dropped. */}
      {primaryAction && !search && !filterChips && !toolbarExtra && (
        rightPortalNode ? (
          createPortal(
            <div className="flex items-center [&_button]:h-8 [&_button]:text-sm">
              {primaryAction}
            </div>,
            rightPortalNode
          )
        ) : (
          <div className="mb-4 flex items-center justify-end [&_button]:h-8 [&_button]:text-sm">
            {primaryAction}
          </div>
        )
      )}

      {(search || filterChips || toolbarExtra) && (
        <div className="mb-4 flex items-center gap-4 overflow-x-auto p-1 -m-1">
          {search && (
            <div className="w-80 shrink-0">
              <Input
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                placeholder={search.placeholder ?? "Search…"}
                className="h-8 text-sm"
              />
            </div>
          )}
          {toolbarExtra && (
            <div className="flex items-center shrink-0">
              {toolbarExtra}
            </div>
          )}
          {filterChips && (
            <FilterChipBar
              options={filterChips.options}
              value={filterChips.value}
              onChange={filterChips.onChange}
              aria-label={filterChips["aria-label"]}
            />
          )}
          {primaryAction && (search || filterChips || toolbarExtra) && (
            <div className="ml-auto shrink-0 flex items-center [&_button]:h-8 [&_button]:text-sm">
              {primaryAction}
            </div>
          )}
        </div>
      )}

      {filterBar ? <div className="mb-4">{filterBar}</div> : null}

      {children}
    </PageContainer>
  );
}
